import { useEffect, useRef, useState } from 'react';
import Input2 from '../../../../components/input/input-2/Input2';
import useDebounce from '../../../../components/hooks/debounce';
import LoaderBar from '../../../../components/loader-bar/LoaderBar';
import RoundedButtonPrimary from '../../../../components/button/rounded-button-primary/RoundedButtonPrimary';
import { Subtitle } from '../../../../components/typography/Heading';
import ButtonText from '../../../../components/button/button-text/ButtonText';
import { formatOrganizationNumber } from '../../../../utils';
import SelectedSchool from './SelectedSchoolWithProjects/SelectedSchool';
import ScreenReaderOnly from '../../../../accessibility/ScreenReaderOnly';
import { searchForSchool } from '../../../../school-event/school-event-actions';
import styles from './SchoolSearch.module.less';
import { useSearchParams } from 'react-router-dom';
import { ownerType, SCHOOL_TYPE } from '../../../../skolelop/school-model';
import { THEMES } from '../../../../utils/cms-utils';
import ThemeContext from '../../../../context/theme-context';
import ThemeContainer from '../../../../components/theme-container/ThemeContainer';
const schoolSearchQueryParamName = 'schoolSearch';

const SchoolSearch = ({
    projectSchool,
    inputClassName,
    type = SCHOOL_TYPE,
    selectSchoolCallback,
    isSchoolLocked,
    themeColor = THEMES.GREY,
    inputLabel,
    pladceholder,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(searchParams?.get(schoolSearchQueryParamName) ?? null);
    const [searchResults, setSearchResults] = useState(null);
    const [chosenSchool, setChosenSchool] = useState(projectSchool);
    const [isSearching, setIsSearching] = useState(false);
    const debouncedSearchInput = useDebounce(searchTerm, 600);
    const schoolInput = useRef(null);

    useEffect(() => {
        if (projectSchool) {
            setChosenSchool(projectSchool);
        }
    }, [projectSchool]);

    useEffect(() => {
        if (searchTerm !== null && debouncedSearchInput === searchTerm) {
            if (debouncedSearchInput) {
                setIsSearching(true);
                searchSchools(debouncedSearchInput);
                setSearchParams(
                    (searchParams) => {
                        searchParams.set(schoolSearchQueryParamName, searchTerm);
                        return searchParams;
                    },
                    { preventScrollReset: true, replace: true }
                );
            } else {
                setSearchResults([]);
                setIsSearching(false);
                setSearchParams(
                    (searchParams) => {
                        searchParams.delete(schoolSearchQueryParamName);
                        return searchParams;
                    },
                    { preventScrollReset: true, replace: true }
                );
            }
        }
    }, [searchTerm, debouncedSearchInput]);

    const searchSchools = async (schoolName) => {
        try {
            const results = await searchForSchool({ type, schoolName });
            results.sort((a, b) => (a.name > b.name ? 1 : -1));
            setSearchResults(results);
        } catch (e) {
            setSearchResults([]);
        }
        setIsSearching(false);
    };

    const onSelectSchool = (school) => {
        setChosenSchool(school);
    };

    return (
        <ThemeContext.Provider value={{ theme: { mainColor: themeColor } }}>
            <div>
                {chosenSchool?.name && (
                    <div className={styles.selectedSchoolContainer}>
                        <SelectedSchool school={chosenSchool} selectSchoolCallback={selectSchoolCallback} type={type} />
                        {!isSchoolLocked && (
                            <ButtonText
                                type="button"
                                className={styles.resetSchoolSelectionButton}
                                onClick={() => {
                                    setChosenSchool(null);
                                    selectSchoolCallback({ school: null });
                                }}
                            >
                                Velg en annen {ownerType(type)}
                            </ButtonText>
                        )}
                    </div>
                )}
                {!chosenSchool?.name && (
                    <>
                        <Input2
                            title={inputLabel || `Finn din ${ownerType(type)}`}
                            value={searchTerm ?? ''}
                            labelClass={inputClassName}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                            }}
                            ref={schoolInput}
                            placeholder={pladceholder}
                        />
                        {debouncedSearchInput && (
                            <ScreenReaderOnly aria-live="polite">{`${
                                searchResults?.length || 0
                            } treff`}</ScreenReaderOnly>
                        )}
                        <ul className={styles.schools}>
                            {searchResults?.map((school) => {
                                const { name, organizationNumber } = school;
                                return (
                                    <ThemeContainer tag={'li'} key={organizationNumber} className={styles.school}>
                                        <div className={styles.schoolInfo}>
                                            <Subtitle tag={'h3'}>{name}</Subtitle>
                                            <div className={styles.organizationNumber}>
                                                Org.nummer {formatOrganizationNumber(organizationNumber)}
                                            </div>
                                        </div>
                                        <div className={styles.schoolButtons}>
                                            <RoundedButtonPrimary
                                                type="button"
                                                aria-label={`Velg «${name}»`}
                                                onClick={() => onSelectSchool(school)}
                                            >
                                                Velg
                                            </RoundedButtonPrimary>
                                        </div>
                                    </ThemeContainer>
                                );
                            })}
                        </ul>
                        {isSearching && (
                            <div>
                                <LoaderBar />
                            </div>
                        )}
                        {!isSearching && debouncedSearchInput !== null && searchResults && searchResults.length === 0 && (
                            <div>
                                <p>
                                    Fant ikke {ownerType(type)}n du søkte etter. Sjekk at du har skrevet navnet på{' '}
                                    {ownerType(type)}n riktig
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </ThemeContext.Provider>
    );
};

export default SchoolSearch;
